import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/api';
import { Button } from '@/components/ui/button';
import { cn, formatRub } from '@/lib/utils';

const links = [
  { to: '/', label: 'Главная', end: true },
  { to: '/shop', label: 'Магазин' },
  { to: '/wiki', label: 'Вики' },
  { to: '/applications', label: 'Заявки' },
  { to: '/rules', label: 'Правила' },
  { to: '/team', label: 'Команда' },
  { to: '/contacts', label: 'Контакты' },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  return (
    <header className="sticky top-0 z-40 w-full bg-eol-bg/85 backdrop-blur-md border-b border-white/5">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="EOL" className="h-9 w-9 rounded" />
          <span className="text-xl font-extrabold text-eol-white tracking-wide">EOL</span>
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                cn(
                  'relative text-sm font-semibold transition',
                  isActive ? 'text-eol-white' : 'text-eol-desc hover:text-eol-white',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 h-[3px] w-8 rounded bg-eol-pink" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {me ? (
            <>
              <NavLink
                to="/profile"
                className={({ isActive }) =>
                  cn(
                    'group relative flex items-center gap-2 transition',
                    isActive ? '' : 'opacity-90 hover:opacity-100',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {me.avatar ? (
                      <img
                        src={me.avatar}
                        alt=""
                        className={cn(
                          'h-8 w-8 rounded-full transition ring-2',
                          isActive ? 'ring-eol-pink' : 'ring-transparent group-hover:ring-white/20',
                        )}
                      />
                    ) : (
                      <div
                        className={cn(
                          'h-8 w-8 rounded-full bg-eol-pink/30 transition ring-2',
                          isActive ? 'ring-eol-pink' : 'ring-transparent',
                        )}
                      />
                    )}
                    <div className="flex flex-col leading-none">
                      <span
                        className={cn(
                          'text-sm font-semibold transition',
                          isActive ? 'text-eol-white' : 'text-eol-white/90 group-hover:text-eol-white',
                        )}
                      >
                        {me.globalName ?? me.username}
                      </span>
                      <span className="text-xs text-eol-pink font-bold mt-0.5">
                        {formatRub(me.balance)}
                      </span>
                    </div>
                  </>
                )}
              </NavLink>
              {me.role === 'admin' && (
                <Button asChild variant="secondary" size="sm">
                  <Link to="/admin">Админка</Link>
                </Button>
              )}
            </>
          ) : (
            <Button asChild>
              <a href={authApi.loginUrl()}>Войти через Discord</a>
            </Button>
          )}
        </div>

        <button
          className="lg:hidden text-eol-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Меню"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-white/5 px-6 py-4 space-y-3">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  'block text-base font-semibold',
                  isActive ? 'text-eol-white' : 'text-eol-desc',
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          {me ? (
            <>
              <Link to="/profile" onClick={() => setOpen(false)} className="block text-eol-pink font-semibold">
                {me.globalName ?? me.username} · {formatRub(me.balance)}
              </Link>
              {me.role === 'admin' && (
                <Link to="/admin" onClick={() => setOpen(false)} className="block text-eol-white font-semibold">
                  Админка
                </Link>
              )}
            </>
          ) : (
            <Button asChild className="w-full">
              <a href={authApi.loginUrl()}>Войти через Discord</a>
            </Button>
          )}
        </div>
      )}
    </header>
  );
}
